import { inject, Injectable } from '@angular/core';
import {
  BaseBoundaryDates,
  GroupHealth,
  Kpi,
  Milestone,
  MilestoneGroup,
  MilestoneGroupName,
  MilestoneName,
  MilestoneStage,
  StageName,
  StudyMilestones,
  StudyMilestonesLabel
} from '@models';

import { CommonFunctionsService } from '../common-functions/common-functions.service';

@Injectable({
  providedIn: 'root'
})
export class StudyGanttChartCalculationService {
  private commonFunctionsService: CommonFunctionsService = inject(CommonFunctionsService);

  private milestoneNames: MilestoneName[] = [
    'Award date',
    'Evidence of agreement',
    'Final protocol received',
    'First study site activated',
    'First subject screened',
    'First subject enrolled',
    '50% sites activated',
    'Last site activated',
    '50% subjects enrolled',
    'Last subject enrolled',
    'Last subject last visit',
    'Database lock',
    'Final TLFs to MW',
    'Final clinical study report delivery',
    'Final tables, listings and figures to medical writer'
  ];

  private stageNames: StageName[] = ['Startup', 'Enrollment', 'Maintenance', 'Closeout'];
  private groupNames: MilestoneGroupName[] = [
    'Site Activation',
    'Subject Enrollment',
    'Subject Visits'
  ];
  getGroupClass(health: string) {
    switch (health) {
      case 'IN_PROGRESS_PAST_DUE':
        return 'group_in_progress_past_due';
      case 'IN_PROGRESS':
        return 'group_in_progress';
      case 'COMPLETED':
        return 'group_completed';
      default:
        return 'group_not_started';
    }
  }

  getGroupHealth(kpi: Kpi | undefined): GroupHealth {
    if (!kpi) {
      return 'NOT_STARTED';
    }

    switch (kpi.health) {
      case 'on-target':
        return 'IN_PROGRESS';
      case 'off-target':
      case 'at-risk':
        return 'IN_PROGRESS_PAST_DUE';
      case 'completed':
        return 'COMPLETED';
      default:
        return 'NOT_STARTED';
    }
  }

  calculateScrollDateFromInProgressStage(stage: MilestoneStage): Date {
    const startDateStringified = stage.startDates.date;
    const endDateStringified = stage.endDates.date;
    const currentDate = new Date();
    if (startDateStringified && endDateStringified) {
      const startDate = new Date(startDateStringified);
      const endDate = new Date(endDateStringified);
      return currentDate >= startDate && currentDate <= endDate ? currentDate : endDate;
    } else {
      return currentDate;
    }
  }

  calculateScrollDateFromStudy(studyMilestones: StudyMilestones): Date | undefined {
    const closeoutStage = studyMilestones.stages.find((stage) => stage.stage === 'Closeout');
    if (closeoutStage) {
      const closeoutEndDate = closeoutStage.endDates.date;
      if (closeoutEndDate) {
        return new Date(closeoutEndDate);
      }
    }

    return undefined;
  }

  getTreeviewFormattedLabel(label: StudyMilestonesLabel, studyMilestones: StudyMilestones) {
    if (this.isStageLabel(label)) {
      return this.getStageLabel(label, studyMilestones);
    } else if (this.isGroupLabel(label)) {
      return this.getGroupLabel(label, studyMilestones);
    } else if (this.isMilestoneLabel(label)) {
      return this.getMilestoneLabel(label, studyMilestones);
    } else return label;
  }

  getMilestoneEndDate(dates: BaseBoundaryDates): string | null {
    const priority: (keyof BaseBoundaryDates)[] = ['actual', 'planned', 'baseline'];
    return priority.map((key) => dates[key]).find((date) => date != null) ?? null;
  }

  studyHasAllCompletedStages(studyMilestones: StudyMilestones): boolean {
    return studyMilestones.stages.every((stage) => stage.status === 'Complete');
  }

  stageIsExpanded(studyHasAllCompletedStages: boolean, stage: MilestoneStage): boolean {
    return studyHasAllCompletedStages ? stage.stage === 'Closeout' : stage.status === 'In Progress';
  }

  isGroupLabel(label: string): label is MilestoneGroupName {
    return this.groupNames.includes(label as MilestoneGroupName);
  }

  private getStageLabel(stageName: string, studyMilestones: StudyMilestones): string {
    const stage = studyMilestones.stages.find((s) => s.stage === stageName);
    const formattedStageName = this.commonFunctionsService.toSentenceCase(stageName);
    if (!stage) {
      return formattedStageName;
    }

    let icon;

    switch (stage.status) {
      case 'In Progress':
        icon = 'on-track-green-bold.svg';
        break;
      case 'Complete':
        icon = 'completed-blue-bold.svg';
        break;
      case 'Upcoming':
        icon = 'upcoming-gray-bold.svg';
        break;
      default:
        break;
    }

    const labelTag = `<label>${formattedStageName}</label>`;

    return icon
      ? `<div class="stage-label"><img src="assets/icons/statuses/${icon}" class="study-gantt-chart-icon"/>${labelTag}</div>`
      : labelTag;
  }

  private getGroupLabel(groupName: string, studyMilestones: StudyMilestones): string {
    const group = this.getGroupByName(studyMilestones, groupName);
    const formattedGroupName = this.commonFunctionsService.toSentenceCase(groupName);
    if (!group) {
      return formattedGroupName;
    }

    let icon;

    if (['Site Activation', 'Subject Enrollment'].includes(group.name)) {
      //TODO: health status values will be updated in the future
      switch (group.health) {
        case 'IN_PROGRESS_PAST_DUE':
          icon = 'past-due-red-bold.svg';
          break;
        case 'IN_PROGRESS':
          icon = 'on-track-green-bold.svg';
          break;
        case 'COMPLETED':
          icon = 'completed-blue-bold.svg';
          break;
        case 'NOT_STARTED':
          icon = 'upcoming-gray-bold.svg';
          break;
        default:
          break;
      }
    } else {
      icon = '';
    }

    const labelTag = `<label>${formattedGroupName}</label>`;

    return icon
      ? `<div class="stage-label"><img src="assets/icons/statuses/${icon}" class="study-gantt-chart-icon"/>${labelTag}</div>`
      : labelTag;
  }

  private getMilestoneLabel(
    milestoneName: MilestoneName,
    studyMilestones: StudyMilestones
  ): string {
    const milestone = this.getMilestoneByName(studyMilestones, milestoneName);
    if (!milestone) {
      return '';
    }

    let icon;

    switch (milestone.status) {
      case 'Completed | Accelerated':
      case 'Completed':
      case 'Completed | Late':
      case 'Completed | On Time':
        icon = 'completed-blue-bold.svg';
        break;
      case 'Past Due':
        icon = 'past-due-red-bold.svg';
        break;
      case 'On Track':
        icon = 'on-track-green-bold.svg';
        break;
      case 'At Risk':
        icon = 'at-risk-orange-bold.svg';
        break;
      case 'Upcoming':
        icon = 'upcoming-gray-bold.svg';
        break;
      default:
        break;
    }

    const labelTag = `<label${icon ? '' : ' class="no-icon"'}>${milestoneName}</label>`;

    return icon
      ? `<img src="assets/icons/statuses/${icon}" class="study-gantt-chart-icon"/>${labelTag}`
      : labelTag;
  }

  private isStageLabel(label: string): label is StageName {
    return this.stageNames.includes(label as StageName);
  }

  private isMilestoneLabel(label: StudyMilestonesLabel): label is MilestoneName {
    return this.milestoneNames.includes(label as MilestoneName);
  }

  private getMilestoneByName(
    studyMilestones: StudyMilestones,
    milestoneName: MilestoneName
  ): Milestone | undefined {
    return this.commonFunctionsService
      .getMilestonesFlatMapFromStages(studyMilestones.stages)
      .find((m) => m.name === milestoneName);
  }

  private getGroupByName(
    studyMilestones: StudyMilestones,
    groupName: string
  ): MilestoneGroup | undefined {
    return studyMilestones.stages
      .flatMap((stage) => stage.milestones)
      .find(
        (milestoneItem): milestoneItem is MilestoneGroup =>
          milestoneItem.type == 'group' && milestoneItem.name == groupName
      );
  }
}
